import { api } from '@ottd/api';
import type { AuthenticatedUser } from '@ottd/api';

import type { LoginPayload, RegisterPayload } from './types';

/*
 * Auth API against Laravel Fortify, prefixed under /api (config/fortify.php
 * `prefix='api'`). The shared @ottd/api client handles the Sanctum CSRF echo,
 * so these are thin POST/PUT wrappers. Only the public app uses the mutations;
 * the shared session (`/api/auth/me`) is what the admin app reuses.
 */

/** `GET /api/auth/me` - current user, or throws ApiError(401) when a guest. */
export function getMe(): Promise<AuthenticatedUser> {
  return api.get<AuthenticatedUser>('/auth/me');
}

/**
 * `POST /api/login`. Fortify returns `{ two_factor: true }` when the account
 * has 2FA enabled (challenge required), otherwise an empty 200 (logged in).
 */
export async function postLogin(payload: LoginPayload): Promise<{ twoFactorRequired: boolean }> {
  const result = await api.post<unknown>('/login', payload);
  const twoFactorRequired =
    typeof result === 'object' &&
    result !== null &&
    'two_factor' in result &&
    (result as { two_factor?: unknown }).two_factor === true;
  return { twoFactorRequired };
}

/** `POST /api/two-factor-challenge` - complete a pending 2FA login. */
export function postTwoFactorChallenge(payload: {
  code?: string;
  recovery_code?: string;
}): Promise<unknown> {
  return api.post<unknown>('/two-factor-challenge', payload);
}

export function postRegister(payload: RegisterPayload): Promise<unknown> {
  return api.post<unknown>('/register', payload);
}

export function postLogout(): Promise<unknown> {
  return api.post<unknown>('/logout');
}

export interface ForgotPasswordPayload {
  email: string;
}

export function postForgotPassword(payload: ForgotPasswordPayload): Promise<{ status?: string }> {
  return api.post<{ status?: string }>('/forgot-password', payload);
}

export interface ResetPasswordPayload {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export function postResetPassword(payload: ResetPasswordPayload): Promise<{ status?: string }> {
  return api.post<{ status?: string }>('/reset-password', payload);
}

/** `POST /api/email/verification-notification` - resend the verify email. */
export function postResendVerification(): Promise<unknown> {
  return api.post<unknown>('/email/verification-notification');
}

export interface UpdateProfilePayload {
  name: string;
  display_name?: string;
  email: string;
}

/** `PUT /api/user/profile-information`. */
export function putProfile(payload: UpdateProfilePayload): Promise<unknown> {
  return api.put<unknown>('/user/profile-information', payload);
}

export interface UpdatePasswordPayload {
  current_password: string;
  password: string;
  password_confirmation: string;
}

/** `PUT /api/user/password`. */
export function putPassword(payload: UpdatePasswordPayload): Promise<unknown> {
  return api.put<unknown>('/user/password', payload);
}

/** `POST /api/account/avatar` (multipart) - returns the served avatar URL. */
export async function postAvatar(file: File): Promise<string> {
  const body = new FormData();
  body.append('avatar', file);
  const res = await api.post<{ data: { avatar: string } }>('/account/avatar', body);
  return res.data.avatar;
}
